// frontend/source/portal/pages/AdminArtTagPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import adminApi from "../../services/adminapi";
import api from "../../services/api";
import CreateArtTagCard from "../components/CreateArtTagCard";
import EditArtTagCard from "../components/EditArtTagCard";
import ConfirmDialog from "../components/ConfirmDialog";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorMessage from "../../components/common/ErrorMessage";
import "./AdminArtTagPage.css";

const AdminArtTagPage = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);
  const [artTags, setArtTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    artTag: null,
  });

  useEffect(() => {
    loadCategoryAndTags();
  }, [categoryId]);

  const loadCategoryAndTags = async () => {
    try {
      setLoading(true);
      const categoryData = await api.getCategoryById(categoryId);
      setCategory(categoryData);

      const tagsData = await api.getArtTagsByCategory(categoryId);
      setArtTags(tagsData.artTags || tagsData);
      setError(null);
    } catch (err) {
      setError("Failed to load art tags. Please try again later.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCategories = () => {
    navigate("/portal");
  };

  const handleDelete = async (artTagId) => {
    const artTag = artTags.find((tag) => tag._id === artTagId);
    setDeleteConfirm({ isOpen: true, artTag });
  };

  const confirmDelete = async () => {
    try {
      await adminApi.deleteArtTag(deleteConfirm.artTag._id);
      setArtTags(artTags.filter((tag) => tag._id !== deleteConfirm.artTag._id));
      setDeleteConfirm({ isOpen: false, artTag: null });
    } catch (err) {
      setError("Failed to delete art tag. Please try again.");
      console.error(err);
      setDeleteConfirm({ isOpen: false, artTag: null });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ isOpen: false, artTag: null });
  };

  const handleUpdate = async (artTagId, updateData) => {
    try {
      const updated = await adminApi.updateArtTag(
        categoryId,
        artTagId,
        updateData
      );
      setArtTags(artTags.map((tag) => (tag._id === artTagId ? updated : tag)));
    } catch (err) {
      setError("Failed to update art tag. Please try again.");
      console.error(err);
      throw err;
    }
  };

  const handleCreate = async (artTagData) => {
    try {
        artTagData.categoryId = categoryId
        const newArtTag = await adminApi.createArtTag(artTagData);
        setArtTags([newArtTag, ...artTags]);
        setError(null);
    } catch (err) {
      setError("Failed to create art tag. Please try again.");
      console.error(err);
      throw err;
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading art tags..." />;
  }

  if (error && !category) {
    return <ErrorMessage message={error} onRetry={loadCategoryAndTags} />;
  }

  return (
    <div className="admin-arttags-main">
      <div className="admin-arttags-content">
        {/* Breadcrumb Navigation */}
        <nav className="admin-arttag-breadcrumb">
          <button
            className="admin-arttag-breadcrumb-link"
            onClick={handleBackToCategories}
            aria-label="Back to categories"
          >
            <svg
              className="admin-arttag-breadcrumb-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            All Categories
          </button>
          <span className="admin-arttag-breadcrumb-separator">/</span>
          <span className="admin-arttag-breadcrumb-current">
            {category?.name || "Loading..."}
          </span>
        </nav>

        <h1 className="admin-arttags-title">
          {category?.name || "Category"} - Manage Art Tags
        </h1>
        <p className="admin-arttags-subtitle">
          Edit, delete, or add art tags for this category
        </p>

        {error && <div className="admin-arttags-error">{error}</div>}

        <div className="admin-arttags-grid">
          <CreateArtTagCard onCreate={handleCreate} />
          {artTags.map((artTag) => (
            <EditArtTagCard
              key={artTag._id}
              artTag={artTag}
              categoryId={categoryId}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
            />
          ))}
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Art Tag?"
        message={`Are you sure you want to delete "${deleteConfirm.artTag?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default AdminArtTagPage;
